// src/pages/CreateSurvey.jsx
import axios from "axios"
import SnackbarComponent from "components/SnackBar"
import CreatableSelect from "react-select/creatable"
import config from "config/config"
import React, { useEffect, useState } from "react"
import { useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"
import {
  Row,
  Col,
  Card,
  CardBody,
  CardTitle,
  Label,
  Input,
  Button,
  FormGroup,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormFeedback,
} from "reactstrap"

/**
 * CreateSurvey
 * - Adds a confirmation modal that requires entering a threshold (1–10) before creating a survey.
 * - The threshold is validated and included in the payload as `threshold`.
 * - If your backend uses a different field name or does not accept it, rename/remove accordingly.
 */
const CreateSurvey = () => {
  const navigate = useNavigate()
  const [err, setErr] = useState(null)
  const [success, setSuccess] = useState(null)
  const [categories, setCategories] = useState([])
  const [thresholdModalOpen, setThresholdModalOpen] = useState(false)
  const [threshold, setThreshold] = useState("")
  const [thresholdTouched, setThresholdTouched] = useState(false)
  const mode = useSelector(state => state.Layout.layoutMode)
  const activeOrganization = useSelector(
    state => state.organizationReducer.activeOrganization,
  )
  const userData = useSelector(state => state.Login.userData)

  const [survey, setSurvey] = useState({
    organizationId: activeOrganization?.id,
    title: "",
    description: "",
    questions: [],
    threshold: undefined, // set when confirmed in modal
  })

  const isValidThreshold = value => {
    const n = Number(value)
    return Number.isInteger(n) && n >= 1 && n <= 10
  }

  const fetchCategories = async () => {
    try {
      const response = await axios.get(
        `${config.backendUrl}/survey/getCategoriesByOrg/${activeOrganization.id}`,
        {
          headers: {
            Authorization: `Bearer ${userData.token}`,
          },
        },
      )
      const categoryNames = (response?.data?.categories || []).map(
        cat => cat.name,
      )
      setCategories(categoryNames)
    } catch (err) {
      console.log(err)
      setErr("Error fetching categories.")
    }
  }

  // Survey inputs
  const handleInputChange = e => {
    const { name, value } = e.target
    setSurvey(prev => ({ ...prev, [name]: value }))
  }

  const handleQuestionChange = (e, questionIndex) => {
    const { name, value } = e.target
    setSurvey(prev => {
      const updatedQuestions = [...prev.questions]
      updatedQuestions[questionIndex] = {
        ...updatedQuestions[questionIndex],
        [name]: value,
      }
      return { ...prev, questions: updatedQuestions }
    })
  }

  const addQuestion = () => {
    setSurvey(prev => ({
      ...prev,
      questions: [
        ...prev.questions,
        {
          questionText: "",
          questionType: "text",
          category: "",
          options: ["", "", "", ""],
        },
      ],
    }))
  }

  const deleteQuestion = questionIndex => {
    setSurvey(prev => ({
      ...prev,
      questions: prev.questions.filter((_, index) => index !== questionIndex),
    }))
  }

  const handleOptionChange = (e, questionIndex, optionIndex) => {
    const { value } = e.target
    setSurvey(prev => {
      const updatedQuestions = [...prev.questions]
      updatedQuestions[questionIndex].options[optionIndex] = value
      return { ...prev, questions: updatedQuestions }
    })
  }

  // Open threshold modal instead of immediate submit
  const openThresholdModal = () => {
    setThresholdModalOpen(true)
  }

  const closeThresholdModal = () => {
    setThresholdModalOpen(false)
    setThreshold("")
    setThresholdTouched(false)
  }

  const confirmAndCreate = async () => {
    setThresholdTouched(true)
    if (!isValidThreshold(threshold)) return

    try {
      const payload = {
        ...survey,
        organizationId: activeOrganization.id, // ensure fresh org id
        threshold: Number(threshold),
      }

      const response = await axios.post(
        `${config.backendUrl}/survey/create`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${userData.token}`,
          },
        },
      )

      setSuccess("Survey Created Successfully!")
      setErr(null)
      setSurvey({
        organizationId: activeOrganization.id,
        title: "",
        description: "",
        questions: [],
        threshold: undefined,
      })
      closeThresholdModal()
    } catch (err) {
      console.log(err)
      setErr("Error Creating Survey!")
      setSuccess(null)
    }
  }

  useEffect(() => {
    if (activeOrganization?.id && userData?.token) {
      fetchCategories()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeOrganization?.id, userData?.token])

  const thresholdInvalid = thresholdTouched && !isValidThreshold(threshold)

  return (
    <React.Fragment>
      <SnackbarComponent
        err={err}
        success={success}
        setErr={setErr}
        setSuccess={setSuccess}
      />

      {/* Threshold Modal */}
      <Modal
        isOpen={thresholdModalOpen}
        toggle={closeThresholdModal}
        backdrop="static"
      >
        <ModalHeader toggle={closeThresholdModal}>
          Set Threshold (1–10)
        </ModalHeader>
        <ModalBody>
          <Label for="thresholdInput">Threshold</Label>
          <Input
            id="thresholdInput"
            type="number"
            min={1}
            max={10}
            step={1}
            value={threshold}
            onChange={e => setThreshold(e.target.value)}
            onBlur={() => setThresholdTouched(true)}
            invalid={thresholdInvalid}
            placeholder="Enter an integer between 1 and 10"
          />
          <FormFeedback>
            Please enter a whole number between 1 and 10.
          </FormFeedback>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={closeThresholdModal}>
            Cancel
          </Button>
          <Button
            color="success"
            onClick={confirmAndCreate}
            disabled={!isValidThreshold(threshold)}
          >
            Confirm & Create
          </Button>
        </ModalFooter>
      </Modal>

      <Row className="justify-content-center">
        <Col xl={12}>
          <Card
            className="shadow-sm br"
            style={{
              backgroundColor: mode === "dark" ? "#252F40" : "#fff",
            }}
          >
            <CardBody>
              <Row className="align-items-center mb-3">
                <Col>
                  <CardTitle
                    className="h4 mb-0 text-uppercase "
                    style={{
                      color: mode === "dark" ? "#FFFFFF" : "#000000",
                    }}
                  >
                    Create Survey
                  </CardTitle>
                </Col>
                <Col className="d-flex justify-content-end">
                  <Button
                    color="danger"
                    size="md"
                    className="mr-2"
                    onClick={() =>
                      setSurvey({
                        organizationId: activeOrganization.id,
                        title: "",
                        description: "",
                        questions: [],
                        threshold: undefined,
                      })
                    }
                  >
                    Reset
                  </Button>
                </Col>
              </Row>

              {/* Survey Title & Description */}
              <Row className="mb-3">
                <Col md={6}>
                  <FormGroup>
                    <Label>Survey Title</Label>
                    <Input
                      type="text"
                      name="title"
                      placeholder="Enter Survey Title"
                      value={survey.title}
                      onChange={handleInputChange}
                    />
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup>
                    <Label>Survey Description</Label>
                    <Input
                      type="text"
                      name="description"
                      placeholder="Enter Survey Description"
                      value={survey.description}
                      onChange={handleInputChange}
                    />
                  </FormGroup>
                </Col>
              </Row>

              {/* Questions Section */}
              <Row>
                <CardTitle
                  className="h4 my-4 text-uppercase "
                  style={{
                    color: mode === "dark" ? "#FFFFFF" : "#000000",
                  }}
                >
                  Questions
                </CardTitle>
              </Row>

              {survey.questions.map((question, questionIndex) => (
                <Row className="mb-3 border rounded p-3" key={questionIndex}>
                  <Col md={5}>
                    <Label>Question</Label>
                    <Input
                      type="text"
                      name="questionText"
                      placeholder="Enter question text"
                      value={question.questionText}
                      onChange={e => handleQuestionChange(e, questionIndex)}
                    />
                  </Col>
                  <Col md={3}>
                    <Label>Type</Label>
                    <Input
                      type="select"
                      name="questionType"
                      value={question.questionType}
                      onChange={e => handleQuestionChange(e, questionIndex)}
                    >
                      <option value="text">Text</option>
                      <option value="rating">Rating</option>
                      <option value="multiple choice">Multiple Choice</option>
                    </Input>
                  </Col>
                  <Col md={3}>
                    <Label>Label</Label>
                    <CreatableSelect
                      isClearable
                      options={categories.map(cat => ({
                        label: cat,
                        value: cat,
                      }))}
                      value={
                        question.category
                          ? {
                              label: question.category,
                              value: question.category,
                            }
                          : null
                      }
                      onChange={selectedOption => {
                        const selectedCategory = selectedOption
                          ? selectedOption.value
                          : ""
                        handleQuestionChange(
                          {
                            target: {
                              name: "category",
                              value: selectedCategory,
                            },
                          },
                          questionIndex,
                        )
                      }}
                      onCreateOption={newCategory => {
                        setCategories(prev => [...prev, newCategory])
                        handleQuestionChange(
                          { target: { name: "category", value: newCategory } },
                          questionIndex,
                        )
                      }}
                      placeholder="Select or create category"
                    />
                  </Col>

                  <Col md={1} className="d-flex align-items-end">
                    <Button
                      color="danger"
                      size="sm"
                      className="mt-3"
                      onClick={() => deleteQuestion(questionIndex)}
                    >
                      Delete
                    </Button>
                  </Col>

                  {/* Multiple Choice Options */}
                  {question.questionType === "multiple choice" && (
                    <Col md={12} className="mt-2">
                      <Label>Options</Label>
                      <Row>
                        {question.options.map((option, optionIndex) => (
                          <Col md={3} key={optionIndex}>
                            <Input
                              type="text"
                              placeholder={`Option ${optionIndex + 1}`}
                              value={option}
                              onChange={e =>
                                handleOptionChange(
                                  e,
                                  questionIndex,
                                  optionIndex,
                                )
                              }
                            />
                          </Col>
                        ))}
                      </Row>
                    </Col>
                  )}
                </Row>
              ))}

              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: "10px",
                }}
                className="d-flex mt-3"
              >
                <Button
                  style={{
                    backgroundColor: "#2990DF",
                  }}
                  className="mr-2"
                  onClick={addQuestion}
                >
                  + Add Question
                </Button>
                <Button color="success" onClick={openThresholdModal}>
                  Submit Survey
                </Button>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </React.Fragment>
  )
}

export default CreateSurvey
